// Pure helpers for formatting visit times in CT and computing arrive-by.
// All "human-entered" times are interpreted as America/Chicago wall-clock,
// regardless of the user's browser timezone.

const CT = "America/Chicago";

export function fmtDate(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: CT,
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(iso));
}

export function fmtTime(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: CT,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

export function fmtTimeCt(iso: string): string {
  return fmtTime(iso) + " CT";
}

export function fmtDateShort(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: CT,
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(iso));
}

export function arriveByIso(startsAtIso: string, leadMinutes: number): string {
  return new Date(new Date(startsAtIso).getTime() - leadMinutes * 60_000).toISOString();
}

export function durationMinutes(startsAtIso: string, endsAtIso: string | null): number {
  if (!endsAtIso) return 60;
  return Math.max(1, Math.round((new Date(endsAtIso).getTime() - new Date(startsAtIso).getTime()) / 60_000));
}

function ctPartsOfUtc(utcMs: number): Record<string, string> {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CT,
    hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(new Date(utcMs));
  const out: Record<string, string> = {};
  for (const p of parts) out[p.type] = p.value;
  return out;
}

/** "YYYY-MM-DDTHH:MM" entered as CT wall-clock → real UTC ISO string. */
export function ctLocalToUtc(local: string): string {
  if (!local) return "";
  // Step 1: pretend the user typed UTC.
  const naiveUtc = Date.parse(local + ":00Z");
  // Step 2: figure out what CT wall-clock corresponds to that UTC instant.
  const ct = ctPartsOfUtc(naiveUtc);
  const ctAsIfUtc = Date.UTC(
    Number(ct.year), Number(ct.month) - 1, Number(ct.day),
    Number(ct.hour), Number(ct.minute), Number(ct.second),
  );
  // Step 3: offset is how far UTC is ahead of CT at that moment.
  const offsetMs = naiveUtc - ctAsIfUtc;
  // Step 4: real UTC instant = naive + offset.
  return new Date(naiveUtc + offsetMs).toISOString();
}

/** UTC ISO → "YYYY-MM-DDTHH:MM" CT wall-clock for <input type="datetime-local"> */
export function utcToCtLocal(iso: string): string {
  if (!iso) return "";
  const ct = ctPartsOfUtc(new Date(iso).getTime());
  return `${ct.year}-${ct.month}-${ct.day}T${ct.hour}:${ct.minute}`;
}
